/**
 * Deterministic executor for scheme setup instructions.
 *
 * Applies a list of SchemeSetupInstruction entries to the game state during
 * setup. Each instruction type has a dedicated handler. Unknown types log a
 * warning and are skipped — the executor never throws.
 *
 * This is separate from scheme twist execution (WP-024), which reacts to
 * events during play.
 */
// ---------------------------------------------------------------------------
// executeSchemeSetup
// ---------------------------------------------------------------------------
/**
 * Applies scheme setup instructions to the game state.
 *
 * Iterates the instruction list with for...of and applies each instruction.
 * Returns a new game state object — pure function, no in-place mutation of
 * the input.
 *
 * @param gameState - The current LegendaryGameState being constructed.
 * @param instructions - Declarative setup instructions from the scheme.
 * @returns Updated LegendaryGameState with instructions applied.
 */
export function executeSchemeSetup(gameState, instructions) {
    // why: scheme setup runs after base deck construction, before the first
    // turn. Instructions configure the board (counters, keywords, city state).
    // This is separate from scheme twist execution (WP-024), which reacts to
    // events during play.
    let result = gameState;
    for (const instruction of instructions) {
        switch (instruction.type) {
            case 'modifyCitySize': {
                // why: D-2602 deferred this while CityZone was a fixed 5-tuple. WP-831 / D-24764 made the
                // City resizable, but no scheme prints a SETUP-time City size (Change the Outcome of WWII
                // resizes it from its Twist), so this setup instruction stays unwired: logged and skipped.
                result = {
                    ...result,
                    messages: [
                        ...result.messages,
                        // why: WP-434 — executeSchemeSetup is a pure immutable update (no pushLog
                        // mutation), so setup narration is appended as a neutral LogEntry literal.
                        {
                            text: `Scheme setup instruction "modifyCitySize" is not yet supported (no scheme sets the City size at setup) — skipped`,
                            outcome: 'neutral',
                        },
                    ],
                };
                break;
            }
            case 'addCityKeyword': {
                // why: schemes may add keywords to City cards at setup time (e.g.,
                // adding Guard to a specific space).
                const keywordValue = instruction.value;
                const cardId = keywordValue.cardId;
                const existingKeywords = result.cardKeywords[cardId] ?? [];
                result = {
                    ...result,
                    cardKeywords: {
                        ...result.cardKeywords,
                        [cardId]: [...existingKeywords, keywordValue.keyword],
                    },
                };
                break;
            }
            case 'addSchemeCounter': {
                // why: schemes may initialize named counters for tracking
                // scheme-specific endgame conditions (e.g., "bystanders rescued"
                // threshold).
                const counterValue = instruction.value;
                const initialValue = counterValue.value ?? 0;
                result = {
                    ...result,
                    counters: {
                        ...result.counters,
                        [counterValue.name]: initialValue,
                    },
                };
                break;
            }
            case 'initialCityState': {
                // why: schemes may pre-populate City spaces before the first villain
                // deck reveal (e.g., starting villain placement).
                const cityValue = instruction.value;
                const newCity = [...result.city];
                newCity[cityValue.cityIndex] = cityValue.cardId;
                result = {
                    ...result,
                    city: newCity,
                };
                break;
            }
            default: {
                // why: unknown instruction types are logged and skipped — never throw.
                // This follows the graceful degradation pattern (D-1234).
                const unknownType = instruction.type;
                result = {
                    ...result,
                    messages: [
                        ...result.messages,
                        // why: WP-434 — neutral LogEntry literal (pure immutable update, no pushLog).
                        {
                            text: `Unknown scheme setup instruction type: "${unknownType}" — skipped`,
                            outcome: 'neutral',
                        },
                    ],
                };
                break;
            }
        }
    }
    return result;
}
//# sourceMappingURL=schemeSetup.execute.js.map